import { useEffect, useState } from "react";
import {
  feedbackApi,
  FeedbackApiTypes,
  FeedbackApiEnums,
} from "@rshbintech.dboul/http";
import { useNotification } from "@rshbintech.dboul/core-ui";
import { Channel, DefaultFormCsi, SubmitForm } from "./types";
import {
  DEFAULT_FORM,
  DEFAULT_FORM_CSI,
  SUCCESSFUL_SUBMIT_TEXT,
} from "./constants";
import { isCsiFormCompleted } from "./utils";

export const useFeedbackFormState = (formKey: string) => {
  const { openNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] =
    useState<FeedbackApiTypes.FeedbackDataForClient | null>(null);

  const channel: Channel = "WEB";

  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    feedbackApi.api
      .getFeedbackformForClient({ key: formKey, channel })
      .then(({ data }) => {
        setData(data);
      });
  }, [formKey]);

  const sendFeedback = async (form: SubmitForm) => {
    setIsLoading(true);

    try {
      await feedbackApi.api.putFeedbackDataFromClient({
        key: formKey,
        channel: FeedbackApiEnums.ChannelEnum.WEB,
        response: JSON.stringify(form),
      });

      setIsLoading(false);

      openNotification(SUCCESSFUL_SUBMIT_TEXT, {
        timeout: 5000,
        type: "primary",
      });
    } catch (error) {
      setIsLoading(false);
      setHasError(true);

      throw error;
    }
  };

  const updateIsClosedFlag = () => {
    feedbackApi.api.putFeedbackDataFromClient({
      key: formKey,
      channel: FeedbackApiEnums.ChannelEnum.WEB,
      isClosed: true,
    });
  };

  const resetError = () => {
    setHasError(false);
  };

  return {
    isLoading,
    data,
    hasError,
    resetError,
    updateIsClosedFlag,
    sendFeedback,
  };
};

export const useFeedbackForm = (formKey: string, schema?: string) => {
  const [isFormCompleted, setIsFormCompleted] = useState(false);
  const isCsiForm = formKey.startsWith("csi-");

  const [form, setForm] = useState<SubmitForm>(
    isCsiForm ? DEFAULT_FORM_CSI : DEFAULT_FORM
  );

  useEffect(() => {
    if (isCsiForm && schema && isCsiFormCompleted(form.score, schema)) {
      setIsFormCompleted(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const onChange = (component: string, value: string | number) => {
    if (component.startsWith("rating-")) {
      if (isCsiForm) {
        const ratingType = component.split("-")[1];
        const id = component.split("-")[2];
        // INFO: Отправка нулевого значения может привести к ошибке вычисления overallScore для CSI формы,
        // поэтому мы заменяем переданные нулевые значения на минимальный рейтинг т.к. эти оценки имеют одинаково негативный эффект
        const notZeroValue = value === 0 ? 1 : value;

        setForm((prevState) => {
          const { score } = prevState as DefaultFormCsi;
          const index = score.findIndex((item) => item.id === id);

          if (index !== -1) {
            const updatedItem = {
              ...score[index],
              [ratingType]: notZeroValue,
            };

            const updatedScore = [...score];

            updatedScore[index] = updatedItem;

            return {
              ...prevState,
              score: updatedScore,
            } as SubmitForm;
          }

          const newItem = {
            id: id,
            [ratingType]: notZeroValue,
          };

          const updatedScore = [...score, newItem];

          return {
            ...prevState,
            score: updatedScore,
          } as SubmitForm;
        });
      } else {
        setIsFormCompleted(true);
        setForm((prevState) => ({ ...prevState, score: value } as SubmitForm));
      }
    }

    if (component.startsWith("textarea-")) {
      setForm((prevState) => ({ ...prevState, comment: value } as SubmitForm));
    }
  };

  return {
    form,
    onChange,
    isFormCompleted,
  };
};
